import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../molecules/ChartFrame/ChartFrame';
import { chartAnimation, gridProps, legendItems, referenceLineProps, tooltipCursor, trendLineProps, xAxisProps, yAxisProps, type TrendSeries } from '../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../molecules/ChartLegend/ChartLegend';
import { ChartTooltip } from '../../molecules/ChartTooltip/ChartTooltip';
import type { CartesianChartProps, ChartRow } from '../../molecules/ChartFrame/chartProps';
import { chartAriaLabel, isEmpty, seriesPayload } from '../../molecules/ChartFrame/chartProps';
import { AnchoredTooltipSurface } from '../../molecules/ChartFrame/AnchoredTooltipSurface';
import { useAnchoredTooltip } from '../../molecules/ChartFrame/useAnchoredTooltip';

/**
 * A line chart, drawn with the Figma chart chrome.
 *
 * Recharts is an implementation detail: pass rows and a series list and the
 * axes, gridlines, legend and tooltip come out styled from the file. Nothing
 * about the plot is specified in Figma — only its chrome is — so the marks
 * below are derived and flagged. See plan.md §7 item 132.
 *
 * ```tsx
 * <LineChart
 *   data={rows}
 *   xKey="month"
 *   series={[{ key: 'revenue', label: 'Revenue' }, { key: 'cost', label: 'Cost' }]}
 * />
 * ```
 */
export type LineChartProps = Omit<CartesianChartProps, 'series'> & {
  /**
   * `TrendSeries` rather than `ChartSeries`, so a series can ask to be drawn
   * dashed (`dash: 'comparison'` for a prior period) or dropped back
   * (`muted`). Both are optional and `ChartSeries` is assignable to
   * `TrendSeries`, so every existing caller is unaffected — a series that asks
   * for neither renders exactly as it did before.
   *
   * Widened here rather than on `CartesianChartProps` so `BarChart`, which has
   * no line to dash, does not start accepting a prop it cannot honour.
   */
  series: TrendSeries[];
  /**
   * Straight segments by default. Figma specifies no curve, and a smoothed line
   * invents readings between the points it was given — `monotone` is available
   * where the reading is genuinely continuous.
   */
  curve?: 'linear' | 'monotone';
  /** Draw a dot at each data point. Off by default; Figma draws no marker. */
  dots?: boolean;
  /**
   * Draw a reference line at this value — a target, benchmark or threshold.
   * Takes `chart/reference` and one dash pattern, so it never reads as data.
   */
  threshold?: number;
  /**
   * Draw a reference line at the **mean of every reading on the plot**, so a
   * point can be read as above or below the period rather than only against the
   * axis. On by default: a line chart is nearly always asked "is this high?",
   * and the answer is a horizontal rule.
   *
   * The mean is taken over every series at once, not per series — one line, one
   * question. On a multi-series plot that is the average reading of the group,
   * which is what a five-brand share chart is compared against anyway.
   *
   * Nulls are skipped, not counted as zero: a bucket nobody was mentioned in is
   * a gap, and averaging it in would drag the rule down towards an axis nothing
   * was measured at.
   *
   * `threshold` wins where both are given. An explicit target is a fact about
   * the business and the average is a fact about the data, and two dashed rules
   * in the same colour would be unreadable — pass `average={false}` to be
   * explicit about it.
   */
  average?: boolean;
};

/**
 * The mean of every reading on the plot, or `undefined` when there are none.
 *
 * Blanks are skipped rather than read as zero — see `average`.
 */
function meanOf(data: ChartRow[], series: TrendSeries[]): number | undefined {
  let sum = 0;
  let count = 0;

  for (const row of data) {
    for (const s of series) {
      const value = Number(row[s.key]);
      if (row[s.key] === null || row[s.key] === undefined || row[s.key] === '') continue;
      if (!Number.isFinite(value)) continue;
      sum += value;
      count += 1;
    }
  }

  return count === 0 ? undefined : sum / count;
}

export function LineChart({
  data,
  xKey,
  series,
  height,
  yDomain,
  yTicks,
  yAxisWidth,
  xTicksTarget,
  formatAxisValue,
  legend = 'bottom',
  tooltip = true,
  loading = false,
  empty,
  emptyLabel,
  formatValue,
  formatDetail,
  xAxisTitle,
  yAxisTitle,
  label,
  curve = 'linear',
  dots = false,
  threshold,
  average = true,
  onViewDetails,
  viewDetailsLabel,
  className,
}: LineChartProps) {
  const items = legendItems(series);

  const anchor = useAnchoredTooltip({ count: data.length, enabled: tooltip, onDrill: onViewDetails });
  const anchored = anchor.point ? data[anchor.point.index] : undefined;

  /* An explicit target wins over a computed one — see `average`. */
  const mean = average && threshold === undefined ? meanOf(data, series) : undefined;

  return (
    <ChartFrame
      height={height}
      xAxisTitle={xAxisTitle}
      yAxisTitle={yAxisTitle}
      loading={loading}
      empty={empty ?? isEmpty(data, series)}
      emptyLabel={emptyLabel}
      label={label ?? chartAriaLabel('Line chart', series, xKey)}
      legend={legend === 'none' ? undefined : <ChartLegend items={items} />}
      legendPlacement="bottom"
      plotRef={anchor.plotRef}
      plotProps={anchor.plotProps}
      hover={
        anchored && (
          <AnchoredTooltipSurface anchor={anchor}>
            <ChartTooltip
              active
              label={anchored[xKey] ?? undefined}
              payload={seriesPayload(anchored, series)}
              formatter={formatValue && ((v) => formatValue(Number(v)))}
              detail={
                formatDetail &&
                ((entry) =>
                  entry.payload ? formatDetail(entry.payload, String(entry.dataKey)) : undefined)
              }
              action={anchor.drill}
              actionLabel={viewDetailsLabel}
              locked={anchor.locked}
            />
          </AnchoredTooltipSurface>
        )
      }
      className={className}
    >
      <RechartsLineChart data={data} {...anchor.chartProps}>
        <CartesianGrid {...gridProps()} />
        <XAxis {...xAxisProps(xKey, data.length, xTicksTarget)} />
        <YAxis
          {...yAxisProps(yAxisWidth)}
          {...(yDomain ? { domain: yDomain, allowDataOverflow: false as const } : {})}
          {...(yTicks ? { ticks: yTicks, interval: 0 as const } : {})}
          tickFormatter={formatAxisValue ?? formatValue}
        />
        {tooltip && (
          /*
           * Kept for its cursor alone — the crosshair under the readout. The
           * bubble is drawn by `ChartFrame`'s `hover` slot now, anchored to the
           * datum instead of dragged along by the pointer, so `content` renders
           * nothing. `cursorProps` pins the crosshair to the locked band, or the
           * line would drift to a band the readout is not about.
           */
          <Tooltip
            {...anchor.cursorProps}
            isAnimationActive={false}
            content={() => null}
            cursor={tooltipCursor('line')}
          />
        )}
        {threshold !== undefined && <ReferenceLine y={threshold} {...referenceLineProps()} />}
        {/*
          * The mean rule carries no caption.
          *
          * It used to be labelled `Avg 6` — the axis' own formatting, above the
          * rule and hard right, on the theory that an unlabelled dashed line
          * cannot be told from a target. In the app it read as clutter instead:
          * the right edge is where the series itself ends, so the caption sat
          * on the last reading of nearly every chart it was drawn on, and the
          * cards that show one of these are small enough that four characters
          * of grey text over the line are the most crowded thing on them.
          * Dropped on the user's call, 2026-09-26; the rule stays, and what it
          * means is the card's to say.
          */}
        {mean !== undefined && <ReferenceLine y={mean} {...referenceLineProps()} />}
        {series.map((s, i) => (
          <Line
            key={s.key}
            type={curve}
            dataKey={s.key}
            name={s.label}
            /*
             * Stroke, weight, dash and opacity all come from `trendLineProps`,
             * which is where the rails for a dashed comparison series already
             * lived. It resolves `strokeWidth` to `chartMarkGeometry.lineStrokeWidth`
             * — the same 2px this line hardcoded before — so a plain series is
             * unchanged. (Derived: Figma specifies no line weight; 2px reads at
             * the 278px plot height the `Chart` slot gives, where 1px vanishes.)
             */
            {...trendLineProps(s, i)}
            dot={dots}
            activeDot={{ r: 4, strokeWidth: 0 }}
            {...chartAnimation}
          />
        ))}
      </RechartsLineChart>
    </ChartFrame>
  );
}
